import React, { FunctionComponent, useState } from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import TimesItem from './components/TimesItem/TimesItem.component';
import { StyledTimes } from './styledComponents';
import { useQueryParams } from 'use-query-params';
import { AppQueryParamsConfigMap } from '../../app/constants';
import { useEventFeed } from '../../api/useEventFeed';
import { useEventTimes } from '../../api/useEventTimes';
import SplashScreenComponent from '../../components/SplashScreen/SplashScreen.component';
import EmptyPageComponent from '../../components/EmptyPage/EmptyPage.component';
import { Helmet } from 'react-helmet';
import { APP_NAME_POSTFIX } from '../../constants';
import { LiveFeedEventTime, Sort } from '../../domain';
import SortIndicator from '../../components/SortIndicator/SortIndicator.component';
import getDisabledProps from './getDisabledProps';
import DownloadTimes from '../../components/DownloadTimes/DownloadTimes.component';
import useDidUpdateEffect from '../../hooks/useDidUpdate';
import {useQueryClient} from 'react-query';

const connector = connect(
  (state: RootState) => ({
    searchQuery: state.app.searchQuery['results'],
  }),
  {},
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Times: FunctionComponent<PropsFromRedux> = ({ searchQuery }) => {
  const [sort, setSort] = useState<Sort>();
  const queryClient = useQueryClient();
  const changeSort = (field: string) => {
    setSort({ field, order: sort?.order === 'asc' ? 'desc' : 'asc' });
  };

  const [{ c: classId, e: eventId }] = useQueryParams(AppQueryParamsConfigMap);
  const { dataUpdatedAt: eventNewsFeedUpdatedAt, data: activeEvent } =
    useEventFeed({
      eventId: eventId,
    });

  const {
    data: times,
    isFetching,
    refetch: refetchTimes,
  } = useEventTimes({
    eventId,
    classId,
    searchQuery,
    sort,
  });

  useDidUpdateEffect(() => {
    // Event refreshes with a given time interval  so we need
    // refetch times when event is refreshed
    refetchTimes();
    queryClient.invalidateQueries('eventTimes');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventNewsFeedUpdatedAt]);

  const renderTitle = () => (
    <Helmet>
      <title>
        {activeEvent?.eventName} Times - {APP_NAME_POSTFIX}
      </title>
    </Helmet>
  );

  let disabledProps = getDisabledProps(activeEvent?.discipline);

  const emptyValueFields = times?.length
    ? Object.keys(times[0]).filter(
        (key) =>
          !times.some(
            (time) =>
              time[key as keyof LiveFeedEventTime] &&
              time[key as keyof LiveFeedEventTime] !== 'N/A',
          ),
      )
    : [];

  disabledProps = disabledProps.concat(emptyValueFields);

  const isEnabled = (field: string) => {
    return !disabledProps.includes(field);
  };

  if (isFetching && !times) {
    return (
      <>
        {renderTitle()}
        <SplashScreenComponent />
      </>
    );
  }

  if (activeEvent?.timesDocUrl && !times?.length) {
    return <DownloadTimes url={activeEvent.timesDocUrl} />;
  }

  if (!times?.length && searchQuery) {
    return (
      <div className="mt-20">
        {renderTitle()}
        <EmptyPageComponent title="Nothing found">
          Try entering just part Rider or Horse Name
        </EmptyPageComponent>
      </div>
    );
  }

  if (!times?.length || !activeEvent?.publishTimesLiveFeed) {
    return (
      <div className="mt-20">
        <EmptyPageComponent title="No times">
          No Times currently published
        </EmptyPageComponent>
      </div>
    );
  }

  return (
    <StyledTimes className="mt-20 overflow-y-auto">
      {renderTitle()}
      <table className="table sticky-head">
        <thead>
          <tr>
            {isEnabled('drawnOrder') && (
              <th scope="col">
                <SortIndicator
                  name="Drawn Order"
                  sort={sort}
                  field="drawnOrder"
                  changeSort={changeSort}
                  disabledFields={[]}
                />
              </th>
            )}
            {isEnabled('eaEventClassAndSort') && (
              <th scope="col">
                <SortIndicator
                  name="Class"
                  sort={sort}
                  field="eaEventClassAndSort"
                  changeSort={changeSort}
                  disabledFields={[]}
                />
              </th>
            )}
            {isEnabled('eaClassEntrySectionName') && (
              <th scope="col">
                <SortIndicator
                  name="Section"
                  sort={sort}
                  field="eaClassEntrySectionName"
                  changeSort={changeSort}
                  disabledFields={[]}
                />
              </th>
            )}

            <th scope="col" colSpan={isEnabled('bridleNumber') ? 3 : 2}>
              <SortIndicator
                name="Rider/BN"
                sort={sort}
                field="bridleNumber"
                changeSort={changeSort}
                disabledFields={disabledProps}
              />
            </th>

            {isEnabled('dressageTime') && (
              <th scope="col">
                <SortIndicator
                  name={
                    isEnabled('showJumpingTime') && isEnabled('dressageTime')
                      ? 'DR Time'
                      : 'Time'
                  }
                  sort={sort}
                  field="dressageTime"
                  changeSort={changeSort}
                  disabledFields={[]}
                />
              </th>
            )}
            {isEnabled('showJumpingTime') && (
              <th scope="col">
                <SortIndicator
                  name={
                    isEnabled('showJumpingTime') && isEnabled('dressageTime')
                      ? 'SJ Time'
                      : 'Time'
                  }
                  sort={sort}
                  field="showJumpingTime"
                  changeSort={changeSort}
                  disabledFields={[]}
                />
              </th>
            )}
            {isEnabled('riderClassStatusName') && (
              <th scope="col">
                <SortIndicator
                  name="Status"
                  sort={sort}
                  field="riderClassStatusName"
                  changeSort={changeSort}
                  disabledFields={[]}
                />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {activeEvent &&
            times?.map((time) => (
              <TimesItem
                time={time}
                disabledProps={disabledProps}
                discipline={activeEvent?.discipline}
              />
            ))}
        </tbody>
      </table>
    </StyledTimes>
  );
};

export default connector(Times);
